/**
 * ⚠️ CRITICAL SECURITY CONTEXT - Microsoft Authentication ⚠️
 *
 * This context handles Microsoft Azure AD authentication.
 *
 * SECURITY REQUIREMENTS:
 * 1. MUST use official Microsoft Authentication Library (MSAL)
 * 2. MUST validate tokens with backend before granting access
 * 3. MUST check for Teams Administrator role
 * 4. MUST NOT create fake tokens or bypass authentication
 * 5. MUST handle token expiration properly with refresh
 *
 * DO NOT:
 * - Add development bypasses
 * - Skip token validation
 * - Allow access without proper authentication
 * - Create mock authentication for testing
 * - Store tokens insecurely
 */
import React, { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { useMsal, useAccount, useIsAuthenticated } from '@azure/msal-react';
import { AccountInfo, InteractionStatus, SilentRequest } from '@azure/msal-browser';
import { loginRequest } from '../config/msalConfig';
import { apiClient } from '../services/apiClient';
import { useConfig } from '../components/ConfigProvider';
import accessTokenService from '../services/accessTokenService';
import UsageTracking from '../tracking';

interface User {
  email: string;
  name: string;
  id: string;
  tenantId: string;
  isTeamsAdmin: boolean;
  directoryRoles?: string[];
  permissions?: {
    policy: {
      read: boolean;
      write: boolean;
    };
    assignment: {
      read: boolean;
      write: boolean;
    };
  };
}

interface AuthError {
  type: 'PERMISSION_DENIED' | 'AUTH_FAILED' | 'NETWORK_ERROR' | 'BACKEND_UNAVAILABLE';
  message: string;
  details?: string;
}

interface MSALAuthContextType {
  user: User | null;
  token: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  isAuthReady: boolean; // NEW: Indicates auth initialization is complete
  error: AuthError | null;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  clearError: () => void;
  clearAuthCache: () => void; // NEW: Manual cache clearing method
}

const MSALAuthContext = createContext<MSALAuthContextType | undefined>(undefined);
const MIN_TOKEN_VALIDITY_SECONDS = 30 * 60; // 30 minutes

export const useMSALAuth = () => {
  const context = useContext(MSALAuthContext);
  if (context === undefined) {
    throw new Error('useMSALAuth must be used within an MSALAuthProvider');
  }
  return context;
};

interface MSALAuthProviderProps {
  children: ReactNode;
}

export const MSALAuthProvider: React.FC<MSALAuthProviderProps> = ({ children }) => {
  const { instance, accounts, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const account = useAccount(accounts && accounts.length > 0 ? accounts[0] : undefined) as AccountInfo | null;
  const { config } = useConfig(); // Get config from context

  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthReady, setIsAuthReady] = useState(false); // NEW: Track auth readiness
  const [error, setError] = useState<AuthError | null>(null);
  const isAcquiringToken = useRef(false);

  // Helper functions that use config from context
  const getApiScopes = useCallback(() => {
    return config ? config.apiScopes : ['User.Read'];
  }, [config]);

  const getApiUrl = useCallback(() => {
    return config ? config.apiUrl : '';
  }, [config]);

  // Function to acquire token silently
  // Ensures token has at least 30 minutes validity for long-running backend processes
  const acquireAppToken = useCallback(async (): Promise<string | null> => {
    console.log('🔑 Attempting to acquire app token...');
    // First try to get account from MSAL state
    const currentAccounts = instance.getAllAccounts();
    console.log('📊 Current accounts in MSAL:', currentAccounts.length);

    const activeAccount = account || (currentAccounts && currentAccounts.length > 0 ? currentAccounts[0] : null);

    if (!activeAccount) {
      console.warn('⚠️ No active account found, attempting interactive login...');
      try {
        // If no account, trigger interactive login
        const loginResponse = await instance.loginPopup({
          scopes: getApiScopes(),
        });
        console.log('✅ Interactive login successful');
        accessTokenService.accessToken = loginResponse.accessToken;
        return loginResponse.accessToken;
      } catch (loginError) {
        console.error('❌ Interactive login failed:', loginError);
        return null;
      }
    }

    try {
      const appRequest: SilentRequest = {
        scopes: getApiScopes(),
        account: activeAccount,
      };
      console.log('🔄 Acquiring token silently for account:', activeAccount.username);
      const response = await instance.acquireTokenSilent(appRequest);
      
      console.log('✅ Token acquired successfully');
      accessTokenService.accessToken = response.accessToken;
      return response.accessToken;
    } catch (silentError: any) {
      console.error('⚠️ Silent token acquisition failed:', silentError);

      // If silent fails, try interactive
      if (silentError?.errorCode === 'interaction_required' ||
          silentError?.errorCode === 'consent_required' ||
          silentError?.errorCode === 'login_required') {
        try {
          console.log('🔄 Attempting interactive token acquisition...');
          const interactiveResponse = await instance.acquireTokenPopup({
            scopes: getApiScopes(),
            account: activeAccount,
          });
          console.log('✅ Interactive token acquisition successful');
          accessTokenService.accessToken = interactiveResponse.accessToken;
          return interactiveResponse.accessToken;
        } catch (interactiveError) {
          console.error('❌ Interactive token acquisition failed:', interactiveError);
          return null;
        }
      }

      return null;
    }finally{
      if(!UsageTracking.Instance?.eventData?.nonPiiData?.["app-id"]){
       await UsageTracking.Instance.initialize();
      }
    }
  }, [account, instance, getApiScopes]);

  useEffect(() => {
    // Don't initialize API client until config is loaded
    if (!config) {
      return;
    }
    
    // Initialize API client with async token getters AND update base URL
    apiClient.setBaseUrl(config.apiUrl);
    apiClient.setAuthTokenGetter(acquireAppToken);
  }, [acquireAppToken, config]);

  // Helper function to acquire app access token
  const acquireAppAccessToken = useCallback(async () => {
    const appRequest: SilentRequest = {
      scopes: getApiScopes(),
      account: account!,
    };

    try {
      return await instance.acquireTokenSilent(appRequest);
    } catch (silentError) {
      // Check for token cache error
      const errorMessage = (silentError as any).message || '';
      if (errorMessage.includes('no_token_request_cache_error')) {
        console.log('Token cache error detected, clearing cache and retrying');
        instance.clearCache();
      }
      // Token expired or not available, get new one interactively
      console.log('Silent token acquisition failed, attempting interactive');
      return await instance.acquireTokenPopup(appRequest);
    }
  }, [account, instance, getApiScopes]);

  // Helper function to build authentication URL
  const buildAuthUrl = useCallback(() => {
    // CRITICAL FIX: Handle different API URL formats between environments
    //
    // Problem: Azure Static Web Apps and localhost have different base URL formats:
    // - Localhost: REACT_APP_API_URL = "http://localhost:3000/api" (includes /api)
    // - Azure: REACT_APP_API_URL = "https://lseg-teams-express.ambitioussand-29f1dd75.eastus.azurecontainerapps.io" (no /api)
    //
    // This caused 404 errors in Azure because we were calling:
    // - Localhost: http://localhost:3000/api/auth/validate-token ✅ CORRECT
    // - Azure: https://lseg-teams-express...io/auth/validate-token ❌ WRONG (missing /api)
    //
    // Solution: Detect if base URL already includes /api and handle accordingly
    const baseUrl = getApiUrl();
    const authEndpoint = '/auth/validate-token';

    if (baseUrl.endsWith('/api')) {
      // Base URL already includes /api (localhost development)
      // Just append the endpoint: /api + /auth/validate-token
      return `${baseUrl}${authEndpoint}`;
    } else {
      // Base URL doesn't include /api (Azure production)
      // Add /api prefix: baseUrl + /api + /auth/validate-token
      return `${baseUrl}/api${authEndpoint}`;
    }
  }, [getApiUrl]);

  // Helper function to handle backend connection errors
  const handleBackendConnectionError = useCallback((accessToken: string) => {
    console.error('🔴 Failed to reach backend');
    // Set minimal auth state to allow frontend to work
    setUser({
      id: account!.homeAccountId,
      email: account!.username,
      name: account!.name || 'User',
      tenantId: account!.tenantId,
      isTeamsAdmin: false // Assume no admin rights if backend is down
    });
    setToken(accessToken);
    setError({
      type: 'BACKEND_UNAVAILABLE',
      message: 'Backend authentication service unavailable',
      details: 'Some features may be limited. Please try again later.'
    });
  }, [account]);

  // Helper function to handle backend response errors
  const handleBackendResponseError = useCallback(async (backendResponse: Response) => {
    if (backendResponse.status === 403) {
      const errorData = await backendResponse.json().catch(() => ({}));
      setError({
        type: 'PERMISSION_DENIED',
        message: 'Access denied - Teams Administrator privileges required',
        details: errorData.error?.message || 'You do not have the necessary Teams Administrator role to access this application.'
      });
      setIsLoading(false);
      return true; // Stop processing
    }

    // For 401 or other auth errors, don't loop - just fail gracefully
    if (backendResponse.status === 401 || backendResponse.status === 500) {
      console.warn('⚠️ Backend validation failed');
      // Don't set partial auth state - this causes loops
      setUser(null);
      setToken(null);
      setError({
        type: 'AUTH_FAILED',
        message: 'Backend validation failed',
        details: 'Unable to validate your credentials with the backend service. Please try again later.'
      });
      return true; // Stop processing
    }

    throw new Error(`Backend authentication failed with status ${backendResponse.status}`);
  }, []);

  // Helper function to process successful backend response
  const processSuccessfulResponse = useCallback(async (data: any, accessToken: string) => {
    if (!data.success) {
      throw new Error(data.error?.message || 'Authentication validation failed');
    }

    // Fetch user roles from /api/auth/me endpoint
    try {
      const authUrl = buildAuthUrl();
      const meEndpoint = authUrl.replace('/api/auth/validate-token', '/api/auth/me');
      const meResponse = await fetch(meEndpoint, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (meResponse.ok) {
        const meData = await meResponse.json();
        if (meData.success && meData.data.roles) {
          // Set user with Teams Admin status, roles, and both tokens

          setUser({
            ...data.data.user,
            isTeamsAdmin: Boolean(data.data.isTeamsAdmin), // Force boolean conversion
            directoryRoles: meData.data.roles.directoryRoles || [],
            permissions: meData.data.permissions || {
              policy: { read: false, write: false },
              assignment: { read: false, write: false }
            }
          });
          setToken(accessToken);
          return;
        }
      }
    } catch (roleError) {
      console.warn('Failed to fetch user roles, continuing without roles:', roleError);
    }

    // Fallback: Set user role with deny-all permissions if /api/auth/me fails
    setUser({
      ...data.data.user,
      isTeamsAdmin: Boolean(data.data.isTeamsAdmin), // Force boolean conversion
      directoryRoles: [],
      permissions: {
        policy: { read: false, write: false },
        assignment: { read: false, write: false }
      }
    });
    setToken(accessToken);
  }, [buildAuthUrl]);

  // Helper function to handle general errors
  const handleGeneralError = useCallback((error: any) => {
    // Check if this is a backend connection error
    if (error instanceof TypeError && error.message === 'Failed to fetch') {
      console.error('🔴 Backend server is not running!');
      setError({
        type: 'BACKEND_UNAVAILABLE',
        message: 'Backend server is not running',
        details: `Cannot connect to backend at ${getApiUrl()}. Please ensure the backend server is running and refresh the page.`
      });
      setUser(null);
      setToken(null);
    } else {
      // Silently handle other errors - no console logging for auth errors
      setUser(null);
      setToken(null);

      // Only set generic error if we haven't already set a specific error (like PERMISSION_DENIED)
      if (!error || (error as any).type !== 'PERMISSION_DENIED') {
        setError({
          type: 'AUTH_FAILED',
          message: 'Authentication failed',
          details: (error as Error).message
        });
      }
    }
  }, [getApiUrl]);

  const acquireTokenAndValidate = useCallback(async () => {
    if (!account || isAcquiringToken.current) return;

    try {
      isAcquiringToken.current = true;
      setIsLoading(true);
      setError(null); // Clear any previous errors

      // Get application access token for our API
      const response = await acquireAppAccessToken();
      const accessToken = response.accessToken;
      // const idToken = response.idToken; // Get the ID token for validation

      // Validate token with backend and check Teams Admin role
      // Send access token for validation since it contains the roles
      let backendResponse;
      try {
        const authUrl = buildAuthUrl();
        console.log('🔐 Calling auth validation endpoint (environment-aware):', authUrl);
        backendResponse = await fetch(authUrl, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ token: accessToken }) // Use access token which has roles
        });
      } catch (fetchError) {
        // Network error - backend might be down or CORS issue
        handleBackendConnectionError(accessToken);
        return;
      }

      if (!backendResponse.ok) {
        const shouldStop = await handleBackendResponseError(backendResponse);
        if (shouldStop) return;
      }

      const data = await backendResponse.json();
      await processSuccessfulResponse(data, accessToken);

    } catch (error) {
      handleGeneralError(error);
    } finally {
      setIsLoading(false);
      setIsAuthReady(true); // NEW: Mark auth as ready regardless of success/failure
      isAcquiringToken.current = false;
    }
  }, [account, acquireAppAccessToken, buildAuthUrl, handleBackendConnectionError, handleBackendResponseError, processSuccessfulResponse, handleGeneralError]);

  // CRITICAL: Handle redirect promise for redirect-based authentication
  useEffect(() => {
    instance.handleRedirectPromise()
      .then((response) => {
        if (response) {
          console.log('✅ Redirect authentication completed successfully');
          // Token acquisition will be handled by the main effect
        }
      })
      .catch((error) => {
        console.error('❌ Redirect promise error:', error);

        // Handle specific MSAL redirect errors
        const errorMessage = error.message || '';
        if (errorMessage.includes('no_token_request_cache_error')) {
          console.log('🔧 Token cache error in redirect, clearing cache');
          instance.clearCache();
          setError({
            type: 'AUTH_FAILED',
            message: 'Token cache error',
            details: 'Please refresh the page and try logging in again.'
          });
        } else {
          setError({
            type: 'AUTH_FAILED',
            message: 'Authentication redirect failed',
            details: error.message
          });
        }
      });
  }, [instance]);

  useEffect(() => {
    // Don't start authentication until config is loaded
    if (!config) {
      return;
    }
    
    if (inProgress === InteractionStatus.None) {
      if (isAuthenticated && account) {
        acquireTokenAndValidate();
      } else {
        setIsLoading(false);
        setIsAuthReady(true); // NEW: Mark auth as ready even if not authenticated
      }
    }
  }, [isAuthenticated, account, inProgress, acquireTokenAndValidate, config]);

  const login = async () => {
    // Prevent multiple concurrent login attempts
    if (inProgress !== InteractionStatus.None) {
      console.log('Authentication already in progress, skipping...');
      return;
    }

    try {
      setIsLoading(true);
      setError(null); // Clear any previous errors

      // Clear cache before login to prevent token cache errors
      await instance.clearCache();

      // for telemetry tracking first login
      sessionStorage.setItem('msal_first_time_login', 'true');

      // Use loginRedirect to avoid COOP popup issues
      await instance.loginRedirect(loginRequest);

      // Note: The page will redirect to Microsoft login
      // Token acquisition will happen in useEffect after redirect back
    } catch (error) {
      sessionStorage.setItem('msal_first_time_login', 'false');
      console.error('Login failed:', error);
      setIsLoading(false);

      // Check for specific MSAL error codes and error messages
      const errorCode = (error as any).errorCode;
      const errorMessage = (error as any).message || '';

      if (errorCode === 'user_cancelled') {
        setError({
          type: 'AUTH_FAILED',
          message: 'Authentication cancelled',
          details: 'You cancelled the login process. Please try again.'
        });
      } else if (errorMessage.includes('no_token_request_cache_error')) {
        // Clear token cache and retry
        instance.clearCache();
        setError({
          type: 'AUTH_FAILED',
          message: 'Token cache error',
          details: 'Authentication cache was cleared. Please try logging in again.'
        });
      } else {
        setError({
          type: 'AUTH_FAILED',
          message: 'Authentication failed',
          details: (error as Error).message
        });
      }
      // Don't throw error for redirect flow
    }
  };

  const logout = async () => {
    // Clear all state first
    sessionStorage.setItem('msal_first_time_login', 'false');
    setUser(null);
    setToken(null);
    setError(null);
    setIsAuthReady(false); // NEW: Reset auth ready state on logout

    // Clear MSAL cache to prevent token cache errors
    await instance.clearCache();

    // Use logoutRedirect to avoid popup, but with account hint for clean logout
    const currentAccount = accounts && accounts.length > 0 ? accounts[0] : undefined;
    await instance.logoutRedirect({
      account: currentAccount,
      postLogoutRedirectUri: window.location.origin,
    }).catch((error) => {
      // If logout fails, at least clear local state and redirect
      console.error('Logout error:', error);
      window.location.href = '/';
    });
  };

  const refreshUser = async () => {
    if (account) {
      await acquireTokenAndValidate();
    }
  };

  const clearError = () => {
    setError(null);
  };

  const clearAuthCache = () => {
    console.log('🔧 Manually clearing authentication cache');
    instance.clearCache();
    setUser(null);
    setToken(null);
    setError(null);
    setIsAuthReady(false);
    // Clear localStorage as well in case MSAL left anything
    localStorage.removeItem('msal.cache');
    localStorage.removeItem('msal.account.keys');
    localStorage.removeItem('msal.token.keys');
  };

  const value: MSALAuthContextType = {
    user,
    token,
    isLoading,
    isAuthenticated: !!user && !!token,
    isAuthReady, // NEW: Provide auth readiness state
    error,
    login,
    logout,
    refreshUser,
    clearError,
    clearAuthCache // NEW: Provide manual cache clearing
  };

  return (
    <MSALAuthContext.Provider value={value}>
      <span hidden data-testid="msal-auth-provider" />
      {children}
    </MSALAuthContext.Provider>
  );
};
